import React from 'react';
import { Button, Card, Form, InputNumber, Select, Space, Table } from 'antd';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { useSkillStudioLayout } from './SkillStudio';

export interface SkillStudioOption {
    value: string | number | undefined;
    label: React.ReactNode;
}

export interface SkillStudioEvalRunsSectionProps<T extends object = Record<string, unknown>> {
    selectedConfig?: string;
    configOptions: SkillStudioOption[];
    onConfigChange: (value?: string) => void;
    selectedTenant?: string;
    tenantOptions: SkillStudioOption[];
    onTenantChange: (value?: string) => void;
    selectedSets: string[];
    setOptions: SkillStudioOption[];
    onSetsChange: (value: string[]) => void;
    questionSetHelp?: React.ReactNode;
    questionSetValidateStatus?: 'success' | 'warning' | 'error' | 'validating';
    selectedAgent?: string;
    agentOptions: SkillStudioOption[];
    onAgentChange: (value?: string) => void;
    selectedQuestionIds: number[];
    questionOptions: SkillStudioOption[];
    onQuestionIdsChange: (value: number[]) => void;
    repeats?: number;
    onRepeatsChange: (value?: number) => void;
    triggering: boolean;
    triggerDisabled?: boolean;
    triggerDisabledReason?: React.ReactNode;
    onTrigger: () => void;
    loading: boolean;
    onRefresh: () => void;
    tableProps: TableProps<T>;
    footerContent?: React.ReactNode;
}

const SkillStudioEvalRunsSection = <T extends object = Record<string, unknown>>({
    selectedConfig,
    configOptions,
    onConfigChange,
    selectedTenant,
    tenantOptions,
    onTenantChange,
    selectedSets,
    setOptions,
    onSetsChange,
    questionSetHelp,
    questionSetValidateStatus,
    selectedAgent,
    agentOptions,
    onAgentChange,
    selectedQuestionIds,
    questionOptions,
    onQuestionIdsChange,
    repeats,
    onRepeatsChange,
    triggering,
    triggerDisabled,
    triggerDisabledReason,
    onTrigger,
    loading,
    onRefresh,
    tableProps,
    footerContent,
}: SkillStudioEvalRunsSectionProps<T>) => {
    const skillStudioLayout = useSkillStudioLayout();
    const sectionHeight = skillStudioLayout != null ? '100%' : 'calc(100vh - 160px)';

    return (
        <div
            style={{
                overflow: 'auto',
                height: skillStudioLayout != null ? sectionHeight : undefined,
                maxHeight: skillStudioLayout != null ? undefined : sectionHeight,
                paddingBottom: 24,
                minHeight: 0,
            }}
        >
            <Card size="small" style={{ marginBottom: 16 }}>
                <Form layout="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <Form.Item label="Config">
                        <Select
                            allowClear
                            placeholder="Default eval_config"
                            style={{ width: 220 }}
                            value={selectedConfig}
                            onChange={onConfigChange}
                            options={configOptions}
                        />
                    </Form.Item>
                    <Form.Item label="Branch">
                        <Select
                            allowClear
                            placeholder="Default published branch"
                            style={{ width: 240 }}
                            value={selectedTenant}
                            onChange={onTenantChange}
                            options={tenantOptions}
                            showSearch
                        />
                    </Form.Item>
                    <Form.Item
                        label="Question Set"
                        required
                        validateStatus={questionSetValidateStatus}
                        help={questionSetHelp}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select question set(s)"
                            style={{ width: 280 }}
                            value={selectedSets}
                            onChange={onSetsChange}
                            options={setOptions}
                            maxTagCount="responsive"
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item label="Target Agent">
                        <Select
                            allowClear
                            placeholder="Full pipeline"
                            style={{ width: 180 }}
                            value={selectedAgent}
                            onChange={onAgentChange}
                            options={agentOptions}
                        />
                    </Form.Item>
                    {questionOptions.length > 0 && (
                        <Form.Item label="Questions" style={{ flex: '1 1 300px' }}>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="All approved questions"
                                style={{ width: '100%', minWidth: 260 }}
                                value={selectedQuestionIds}
                                onChange={onQuestionIdsChange}
                                maxTagCount="responsive"
                                optionFilterProp="label"
                                options={questionOptions}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label="Repeats">
                        <InputNumber
                            min={2}
                            placeholder="—"
                            style={{ width: 80 }}
                            value={repeats}
                            onChange={(v) => onRepeatsChange(v ?? undefined)}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            loading={triggering}
                            disabled={triggerDisabled}
                            onClick={onTrigger}
                        >
                            Run Evaluation
                        </Button>
                        {triggerDisabledReason && (
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                {triggerDisabledReason}
                            </div>
                        )}
                    </Form.Item>
                </Form>
            </Card>

            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} size="small">
                        Refresh
                    </Button>
                </Space>
            </div>

            <Table {...tableProps} />
            {footerContent}
        </div>
    );
};

export default SkillStudioEvalRunsSection;
